import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';

function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const hasVerified = useRef(false);

  useEffect(() => {
    // Prevent multiple verification attempts
    if (hasVerified.current) {
      return;
    }

    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. No token provided.');
      return;
    }

    // Call API to verify email
    const verifyEmail = async () => {
      try {
        hasVerified.current = true;

        // api.get() returns parsed JSON on success, throws on error
        await api.get(`/api/auth/verify-email?token=${token}`);

        // If we get here, verification succeeded
        setStatus('success');
        setMessage('Email verified successfully!');

      } catch (error) {
        setStatus('error');

        // Extract error message (handles various error formats)
        let errorMessage = 'Verification failed. Token may be expired or invalid.';

        if (error.response?.data?.error) {
          // API returned error in response.data.error
          errorMessage = error.response.data.error;
        } else if (error.response?.data?.message) {
          // API returned error in response.data.message
          errorMessage = error.response.data.message;
        } else if (error.message) {
          // Generic error message
          errorMessage = error.message;
        }

        setMessage(errorMessage);
      }
    };

    verifyEmail();
  }, [searchParams]);

  const handleResendVerification = async (e) => {
    e.preventDefault();

    if (!resendEmail) {
      alert('Please enter your email address');
      return;
    }

    setResending(true);

    try {
      // api.post() returns parsed JSON on success, throws on error
      await api.post('/api/auth/resend-verification', {
        email: resendEmail
      });

      alert('Verification email sent! Please check your inbox.');
      setResendEmail('');

    } catch (error) {

      // Extract error message
      let errorMessage = 'Failed to resend verification email.';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(errorMessage);

    } finally {
      setResending(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {status === 'loading' && (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <h2 style={styles.title}>Verifying your email...</h2>
            <p style={styles.text}>Please wait a moment</p>
          </div>
        )}

        {status === 'success' && (
          <div style={styles.successContainer}>
            <div style={styles.checkmark}>✓</div>
            <h2 style={styles.title}>Email Verified Successfully!</h2>
            <p style={styles.text}>{message}</p>
            <p style={styles.text}>Your email has been confirmed. You can now access all features.</p>

            <button
              onClick={() => navigate('/dashboard')}
              style={styles.continueButton}
            >
              Continue to Dashboard
            </button>
          </div>
        )}

        {status === 'error' && (
          <div style={styles.errorContainer}>
            <div style={styles.errorIcon}>⚠</div>
            <h2 style={styles.title}>Verification Failed</h2>
            <p style={styles.errorText}>{message}</p>

            <div style={styles.resendSection}>
              <h3 style={styles.resendTitle}>Resend Verification Email</h3>
              <form onSubmit={handleResendVerification} style={styles.form}>
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  style={styles.input}
                  required
                />
                <button
                  type="submit"
                  style={styles.resendButton}
                  disabled={resending}
                >
                  {resending ? 'Sending...' : 'Resend Verification Email'}
                </button>
              </form>
            </div>

            <div style={styles.linksContainer}>
              <a href="/login" style={styles.link}>Back to Login</a>
              <span style={styles.separator}>|</span>
              <a href="/register" style={styles.link}>Create New Account</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    padding: '20px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
    textAlign: 'center',
  },
  loadingContainer: {
    padding: '20px',
  },
  spinner: {
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #007bff',
    borderRadius: '50%',
    width: '50px',
    height: '50px',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px',
  },
  successContainer: {
    padding: '20px',
  },
  checkmark: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#28a745',
    color: 'white',
    fontSize: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    fontWeight: 'bold',
  },
  errorContainer: {
    padding: '20px',
  },
  errorIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#ffc107',
    color: 'white',
    fontSize: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    fontWeight: 'bold',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '15px',
    color: '#333',
  },
  text: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '10px',
  },
  continueButton: {
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    color: 'white',
    backgroundColor: '#28a745',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginTop: '20px',
    transition: 'background-color 0.3s',
  },
  errorText: {
    fontSize: '16px',
    color: '#dc3545',
    marginBottom: '30px',
  },
  resendSection: {
    marginTop: '30px',
    marginBottom: '20px',
    padding: '20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
  },
  resendTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '15px',
    color: '#333',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  input: {
    padding: '12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    outline: 'none',
  },
  resendButton: {
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: '600',
    color: 'white',
    backgroundColor: '#007bff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
  linksContainer: {
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
  },
  link: {
    color: '#007bff',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
  },
  separator: {
    color: '#ddd',
  },
};

// Add keyframe animation for spinner
const styleSheet = document.styleSheets[0];
const keyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
styleSheet.insertRule(keyframes, styleSheet.cssRules.length);

export default VerifyEmailPage;
